import { ViewTypeName } from '@air/api/types';
import {
  CheckIcon,
  LayoutGridIcon as GridIcon,
  type IconComponent,
  SquareKanbanIcon,
  Table2Icon,
} from '@air/design-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';

import { DropdownMenuWithActionSheet } from '~/components/Menus/DropdownMenuWithActionSheet';
import { preventCloseAutoFocusIfFocusMovedOutside } from '~/utils/preventCloseAutoFocusIfFocusMovedOutside';

export type ViewTypeToggleMenuComponentProps = {
  availableViewTypeNames: ViewTypeName[];
  currentViewTypeName: ViewTypeName;
  dataTestId?: string;
  onChange: (nextViewTypeName: ViewTypeName) => void;
};

type ViewTypeView = {
  icon: IconComponent;
  label: string;
};

const VIEW_TYPE_VIEW_MAP: Record<ViewTypeName, ViewTypeView> = {
  [ViewTypeName.gallery]: {
    icon: GridIcon,
    label: 'Gallery',
  },
  [ViewTypeName.table]: {
    icon: Table2Icon,
    label: 'Table',
  },
  [ViewTypeName.kanban]: {
    icon: SquareKanbanIcon,
    label: 'Kanban',
  },
};

export const ViewTypeToggleMenuComponent = ({
  availableViewTypeNames,
  onChange,
  currentViewTypeName,
  dataTestId,
}: ViewTypeToggleMenuComponentProps) => {
  const { icon: currentIcon, label: currentLabel } = VIEW_TYPE_VIEW_MAP[currentViewTypeName];

  const viewTypeOptions: DropdownMenuOption[] = availableViewTypeNames.map((viewTypeName) => {
    const { icon: OptionIcon, label } = VIEW_TYPE_VIEW_MAP[viewTypeName];

    return {
      id: viewTypeName,
      label: label,
      onSelect: () => onChange(viewTypeName),
      prefix: <OptionIcon size="sm" />,
      suffix: viewTypeName === currentViewTypeName ? <CheckIcon size="sm" /> : null,
      type: 'item',
    };
  });

  // The primitive renders a title option as a label (not a menu item), so it doesn't shift item ids or selection.
  const options: DropdownMenuOption[] = [{ id: 'view-as', label: 'View as', type: 'title' }, ...viewTypeOptions];

  return (
    <DropdownMenuWithActionSheet
      align="end"
      onCloseAutoFocus={preventCloseAutoFocusIfFocusMovedOutside}
      options={options}
      size="small"
      trigger={
        <IconButton
          appearance="ghost"
          color="gray"
          label={currentLabel}
          adsIcon
          icon={currentIcon}
          size="small"
          data-testid={dataTestId}
        />
      }
      title="View Type"
    />
  );
};
